import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { skipToken } from '@reduxjs/toolkit/query';
import { DATA_SET_PAGINATION_COUNT, SORT_DIRECTION_INDICATOR } from 'constants/dataSets';
import { DEFAULT_ORDER_BY } from 'constants/general';
import i18n from 'i18n';
import { useAppSelector } from 'store/hooks/useApp';
import { selectStakeholderVcTab } from 'store/slices/general/selectors';
import { setStakeholderVcTab } from 'store/slices/general/slice';
import { useGetSingleMAInitiateQuery } from 'store/slices/materialAssessment/apis/materialAssessmentApi';
import { useDeleteInsightMutation, useGetInsightQuery } from 'store/slices/survey/apis/surveyApi';

import { ORDER_KEY, ORDER_TYPE, SORT } from '../constants/formFields';
import { ORDERS } from '../constants/interfaces';
import useMaterialityAssessmentSurveyListForm
  from '../pages/MaterialAssessmentPages/MaterialAssessmentSurveyCreation/hooks/useMaterialityAssessmentSurveyListForm';

import type { TOrderByIdentifier } from '../constants/interfaces';
import type ISurveyInsightItem from '../store/slices/survey/interfaces/ISurveyInsightItem';

const useMaterialityAssessmentInsight = (insightType: string) => {
  const dispatch = useDispatch();
  const [orderBy, setOrderBy] = useState(DEFAULT_ORDER_BY);
  const [listInsights, setListInsights] = useState<ISurveyInsightItem[]>([]);
  const [listInsightsKey, setListInsightsKey] = useState('');
  const [offsetMain, setOffsetMain] = useState(0);
  const stakeholderVcTab = useAppSelector(selectStakeholderVcTab);

  const { recordId } = useParams();

  const {
    control, getValues, setValue,
  } = useMaterialityAssessmentSurveyListForm();

  const shouldSkipInsightQuery = !recordId || stakeholderVcTab === null;
  const insightQueryArg = shouldSkipInsightQuery
    ? skipToken
    : {
      orderBy: orderBy ? [orderBy] : undefined,
      offset: offsetMain,
      insightType,
      maId: Number(recordId),
      limit: DATA_SET_PAGINATION_COUNT,
      valueChainMapId: stakeholderVcTab,
    };

  const {
    currentData,
    isFetching: isInsightListFetching,
    isLoading: isInsightListInitialLoading,
  } = useGetInsightQuery(insightQueryArg);

  const { data: singleMA } = useGetSingleMAInitiateQuery({ id: recordId || '' });
  const [deleteSurvey] = useDeleteInsightMutation();

  const { meta, items } = currentData?.data || {};
  const currentInsightsCount = meta?.count ?? items?.length ?? 0;
  const hasCurrentInsightData = !!currentData;
  const isInsightListLoading = isInsightListFetching || isInsightListInitialLoading;
  const isEmptyList = hasCurrentInsightData && currentInsightsCount === 0 && !isInsightListFetching;
  const hasMore = meta?.hasNext || false;
  const currentListKey = [recordId || '', insightType, stakeholderVcTab || '', orderBy].join('-');
  const fallbackItemsList = listInsightsKey === currentListKey ? listInsights : [];
  const itemsList = hasCurrentInsightData && offsetMain === 0 && items
    ? items
    : fallbackItemsList;

  useEffect(() => {
    if (singleMA && singleMA?.data?.valueChainMaps && singleMA?.data?.valueChainMaps?.length > 0
      && stakeholderVcTab === null) {
      dispatch(setStakeholderVcTab(singleMA.data.valueChainMaps[0].id));
    }
  }, [singleMA, dispatch, stakeholderVcTab]);

  useEffect(() => {
    setOffsetMain(0);
    setListInsights([]);
    setListInsightsKey(currentListKey);
  }, [currentListKey]);

  useEffect(() => {
    if (!items) {
      return;
    }

    setListInsightsKey(currentListKey);
    setListInsights((prev) => {
      const prevIds = new Set(prev.map((item) => item.id));
      const newItems = items.filter((item) => !prevIds.has(item.id));

      if (offsetMain === 0) {
        return items;
      }

      return [...prev, ...newItems];
    });
  }, [currentListKey, items, offsetMain]);

  const onDelete = (id: number) => {
    deleteSurvey({ id })
      .unwrap()
      .then(() => {
        toast.success(i18n.t('notifications.surveyHasBeenDeleted'));
        setListInsights((prev) => prev.filter((item) => item.id !== id));
      })
      .catch(() => {
        toast.error(i18n.t('notifications.errorDeletingSurvey'));
      });
  };

  const onSortChange = (orderByIdentifier: TOrderByIdentifier) => {
    const { sort: { orderKey, orderType } } = getValues();
    const isAsc = orderKey === orderByIdentifier && orderType === ORDERS.asc;
    setValue(SORT, { [ORDER_KEY]: orderByIdentifier, [ORDER_TYPE]: isAsc ? ORDERS.desc : ORDERS.asc });
    const order = isAsc ? `${SORT_DIRECTION_INDICATOR}${orderByIdentifier}` : orderByIdentifier;
    setOffsetMain(0);
    setListInsights([]);
    setOrderBy(order);
  };

  const loadMoreInsights = () => {
    if (meta?.hasNext) {
      setOffsetMain((prev) => prev + DATA_SET_PAGINATION_COUNT);
    }
  };

  const handleClickVcTab = (vcId: number) => {
    dispatch(setStakeholderVcTab(vcId));
  };

  return {
    isEmptyList,
    isLoading: isInsightListLoading,
    control,
    onSortChange,
    itemsList,
    hasNext: !!meta?.hasNext,
    onDelete,
    loadMoreInsights,
    hasMore,
    activeVcTab: stakeholderVcTab || null,
    handleClickVcTab,
    currentInsightsCount,
    isCurrentInsightDataReady: hasCurrentInsightData && !isInsightListFetching,
  };
};

export default useMaterialityAssessmentInsight;
